import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  AppBar, Toolbar, IconButton, Typography, Box,
  Menu, MenuItem, Divider, useMediaQuery, alpha,
} from "@mui/material";
import MenuIcon       from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon      from "@mui/icons-material/Check";
import logoTRS from '../assets/logo_portatil3.png';
import MARCA from '../config/marca';

// ── Iconos de grupo (nivel 0) ──
import EventIcon            from "@mui/icons-material/Event";
import PeopleIcon           from "@mui/icons-material/People";
import DevicesIcon          from "@mui/icons-material/Devices";
import ReceiptLongIcon      from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon   from "@mui/icons-material/AccountBalance";
import GroupsIcon           from "@mui/icons-material/Groups";
import AssessmentIcon       from "@mui/icons-material/Assessment";
import AppsIcon             from "@mui/icons-material/Apps";
import AssignmentIcon       from "@mui/icons-material/Assignment";
import DirectionsCarIcon    from "@mui/icons-material/DirectionsCar";
import RadioIcon            from "@mui/icons-material/Radio";
import RouterIcon           from "@mui/icons-material/Router";
import SimCardIcon          from "@mui/icons-material/SimCard";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import InventoryIcon        from "@mui/icons-material/Inventory";
import SupportAgentIcon     from "@mui/icons-material/SupportAgent";
import SmsIcon              from "@mui/icons-material/Sms";
import SmartToyIcon         from "@mui/icons-material/SmartToy";
import NotificationsIcon    from "@mui/icons-material/Notifications";
import SettingsIcon         from "@mui/icons-material/Settings";
import RequestPageIcon      from "@mui/icons-material/RequestPage";
import PaymentsIcon         from "@mui/icons-material/Payments";
import SatelliteIcon        from "@mui/icons-material/Satellite";
import ComputerIcon         from "@mui/icons-material/Computer";

// ── Iconos de hijos (nivel 1) ──
import DashboardIcon        from "@mui/icons-material/Dashboard";
import CalendarMonthIcon    from "@mui/icons-material/CalendarMonth";
import ManageAccountsIcon   from "@mui/icons-material/ManageAccounts";
import FolderOpenIcon       from "@mui/icons-material/FolderOpen";
import SendIcon             from "@mui/icons-material/Send";
import StickyNote2Icon      from "@mui/icons-material/StickyNote2";
import FolderSharedIcon     from "@mui/icons-material/FolderShared";
import TaskIcon             from "@mui/icons-material/Task";
import BookIcon             from "@mui/icons-material/Book";
import EngineeringIcon      from "@mui/icons-material/EngineeringOutlined";
import ShoppingCartIcon     from "@mui/icons-material/ShoppingCart";
import PriceCheckIcon       from "@mui/icons-material/PriceCheck";
import ScheduleIcon         from "@mui/icons-material/Schedule";
import AppRegistrationIcon  from "@mui/icons-material/AppRegistration";
import HandymanIcon         from "@mui/icons-material/Handyman";
import WalletIcon           from "@mui/icons-material/Wallet";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalAtmIcon         from "@mui/icons-material/LocalAtm";
import ConstructionIcon     from "@mui/icons-material/Construction";
import ApartmentIcon        from "@mui/icons-material/ApartmentOutlined";
import PersonIcon           from "@mui/icons-material/Person";
import EventNoteIcon        from "@mui/icons-material/EventNote";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HowToRegIcon         from "@mui/icons-material/HowToReg";
import StorageIcon          from "@mui/icons-material/Storage";
import PollIcon             from "@mui/icons-material/Poll";
import PhoneIcon            from "@mui/icons-material/Phone";
import QuizIcon             from "@mui/icons-material/Quiz";
import PublicIcon           from "@mui/icons-material/Public";
import MyLocationIcon       from "@mui/icons-material/MyLocation";
import CompareArrowsIcon    from "@mui/icons-material/CompareArrows";
import SearchIcon           from "@mui/icons-material/Search";
import TuneIcon             from "@mui/icons-material/Tune";
import QueryStatsIcon       from "@mui/icons-material/QueryStats";
import SummarizeIcon        from "@mui/icons-material/Summarize";
import BuildIcon            from "@mui/icons-material/Build";
import BarChartIcon         from "@mui/icons-material/BarChart";
import HistoryIcon          from "@mui/icons-material/History";
import PersonSearchIcon     from "@mui/icons-material/PersonSearch";
import ContactPhoneIcon     from "@mui/icons-material/ContactPhone";
import NewspaperIcon        from "@mui/icons-material/Newspaper";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ReceiptIcon          from "@mui/icons-material/Receipt";
import AccountTreeIcon      from "@mui/icons-material/AccountTree";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LocationOnIcon       from "@mui/icons-material/LocationOn";
import SpeedIcon            from "@mui/icons-material/Speed";
import PieChartIcon         from "@mui/icons-material/PieChart";
import ListAltIcon          from "@mui/icons-material/ListAlt";
import CircleIcon           from "@mui/icons-material/Circle";

import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import Notifications          from "../components/Notifications";
import UserAvatar             from "../components/UserAvatar";
import GlobalSearchBox        from "../components/layout/GlobalSearchBox";
import axios                  from "../config/axiosConfig";
import { ThemeContext }       from "../context/ThemeContext";

// ─────────────────────────── Roles helpers ───────────────────────────
const ROLES = ["admin", "tecnico", "sistemas", "conta", ""];
const canSee = (userRole, allowed = ROLES) => {
  if (!userRole) return false;
  if (userRole === "admin") return true;
  return allowed.includes(userRole);
};
const filterByRole = (sections, role) =>
  (sections || [])
    .map((s) => {
      const children = (s.children || []).filter((c) => canSee(role, c.roles));
      if (!canSee(role, s.roles) && children.length === 0) return null;
      return { ...s, children };
    })
    .filter(Boolean);

const findSectionIndex = (pathname, sections) => {
  const starts = (base) => pathname === base || pathname.startsWith(base + "/");
  const idx = (sections || []).findIndex(
    (s) => starts(s.to) || (s.children || []).some((c) => starts(c.to))
  );
  return idx === -1 ? 0 : idx;
};

// ─────────────────────────── Icon helpers ───────────────────────────
const slug = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const GROUP_COLORS = {
  agenda: "#3B82F6", cliente: "#10B981", equipo: "#8B5CF6",
  factura: "#F59E0B", contabilidad: "#EF4444",
  rrhh: "#EC4899", reporte: "#06B6D4", plataforma: "#6366F1",
  requerim: "#F97316", vehiculo: "#84CC16", radio: "#14B8A6",
  modem: "#A855F7", chip: "#64748B", bateria: "#22C55E",
  inventario: "#F59E0B", reclamo: "#EF4444", sms: "#3B82F6",
  chatbot: "#8B5CF6", notif: "#F97316", banco: "#10B981",
  presupuesto: "#06B6D4", wialon: "#0EA5E9", leasecontrol: "#0EA5E9",
  flespi: "#7C3AED", sistema: "#64748B", default: "#6366F1",
};
const getGroupColor = (label = "") => {
  const s = slug(label);
  for (const [key, color] of Object.entries(GROUP_COLORS))
    if (s.includes(key)) return color;
  return GROUP_COLORS.default;
};

const groupIconByLabel = (label = "") => {
  const s = slug(label);
  if (s.includes("agenda"))       return EventIcon;
  if (s.includes("cliente"))      return PeopleIcon;
  if (s.includes("equipo"))       return DevicesIcon;
  if (s.includes("factura"))      return ReceiptLongIcon;
  if (s.includes("contabilidad")) return AccountBalanceIcon;
  if (s.includes("rr.hh") || s.includes("rrhh")) return GroupsIcon;
  if (s.includes("reporte"))      return AssessmentIcon;
  if (s.includes("plataforma"))   return AppsIcon;
  if (s.includes("requerim"))     return AssignmentIcon;
  if (s.includes("vehiculo"))     return DirectionsCarIcon;
  if (s.includes("radio"))        return RadioIcon;
  if (s.includes("modem"))        return RouterIcon;
  if (s.includes("chip") || s.includes("sim")) return SimCardIcon;
  if (s.includes("bateria"))      return BatteryChargingFullIcon;
  if (s.includes("inventario"))   return InventoryIcon;
  if (s.includes("reclamo"))      return SupportAgentIcon;
  if (s.includes("sms"))          return SmsIcon;
  if (s.includes("chatbot"))      return SmartToyIcon;
  if (s.includes("notif"))        return NotificationsIcon;
  if (s.includes("banco"))        return AccountBalanceIcon;
  if (s.includes("presupuesto"))  return RequestPageIcon;
  if (s.includes("pago"))         return PaymentsIcon;
  if (s.includes("wialon") || s.includes("leasecontrol") || s.includes("rastreo") || s.includes("flespi")) return SatelliteIcon;
  if (s.includes("sistema"))      return ComputerIcon;
  if (s.includes("config"))       return SettingsIcon;
  return AppsIcon;
};

const childIconByLabel = (label = "") => {
  const s = slug(label);
  if (s.includes("dashboard"))                          return DashboardIcon;
  if (s.includes("agenda") || s.includes("calendario")) return CalendarMonthIcon;
  if (s === "administrar" || (s.includes("administracion") && !s.includes("archivo") && !s.includes("personal"))) return ManageAccountsIcon;
  if (s.includes("documento"))                          return FolderOpenIcon;
  if (s.includes("envio"))                              return SendIcon;
  if (s.includes("nota"))                               return StickyNote2Icon;
  if (s.includes("proyecto") && !s.includes("modulo")) return FolderSharedIcon;
  if (s.includes("solicitud") && !s.includes("permiso") && !s.includes("aprobada")) return TaskIcon;
  if (s.includes("bitacora"))                           return BookIcon;
  if (s === "ot" || (s.includes("ot") && s.length <= 4)) return EngineeringIcon;
  if (s.includes("requerimient") && s.includes("sistema")) return ComputerIcon;
  if (s.includes("requerimient"))                       return AssignmentIcon;
  if (s.includes("orden") && s.includes("compra"))      return ShoppingCartIcon;
  if (s.includes("precio"))                             return PriceCheckIcon;
  if (s.includes("programacion"))                       return ScheduleIcon;
  if (s.includes("registrar") && s.includes("modelo")) return AppRegistrationIcon;
  if (s.includes("registrar") && s.includes("equipo")) return HandymanIcon;
  if (s.includes("caja") && s.includes("instalac"))     return ConstructionIcon;
  if (s.includes("caja") && s.includes("proyecto"))     return AccountBalanceWalletIcon;
  if (s.includes("caja") && s.includes("tecnic"))       return BuildIcon;
  if (s.includes("caja") && s.includes("viatico"))      return LocalAtmIcon;
  if (s.includes("caja"))                               return WalletIcon;
  if (s.includes("modulo") && s.includes("proyecto"))   return ApartmentIcon;
  if (s.includes("administracion") && s.includes("personal")) return PersonIcon;
  if (s.includes("planilla"))                           return EventNoteIcon;
  if (s.includes("solicitud") && s.includes("aprobada")) return CheckCircleOutlineIcon;
  if (s.includes("solicitud") && s.includes("permiso")) return HowToRegIcon;
  if (s.includes("solicitudes") && s.includes("permiso")) return HowToRegIcon;
  if (s.includes("administracion") && s.includes("archivo")) return FolderSharedIcon;
  if (s.includes("busqueda") && s.includes("servidor")) return StorageIcon;
  if (s.includes("invitacion") || s.includes("encuesta")) return PollIcon;
  if (s.includes("nomina"))                             return SummarizeIcon;
  if (s.includes("produccion"))                         return QueryStatsIcon;
  if (s.includes("llamada"))                            return PhoneIcon;
  if (s.includes("cuestionario"))                       return QuizIcon;
  if (s.includes("reporte") && s.includes("proceso"))   return QueryStatsIcon;
  if (s.includes("ypfb") && s.includes("nacion"))       return PublicIcon;
  if (s.includes("ypfb") && s.includes("intern"))       return SatelliteIcon;
  if (s.includes("trs") && s.includes("rastreo"))       return MyLocationIcon;
  if (s.includes("flespi"))                             return RouterIcon;
  if (s.includes("comparacion") || s.includes("comparar")) return CompareArrowsIcon;
  if (s.includes("busqueda") && s.includes("unidad"))   return SearchIcon;
  if (s.includes("cliente") && !s.includes("servicio")) return PersonSearchIcon;
  if (s.includes("contacto"))                           return ContactPhoneIcon;
  if (s.includes("noticia") || s.includes("news"))      return NewspaperIcon;
  if (s.includes("egreso"))                             return RemoveCircleOutlineIcon;
  if (s.includes("factura"))                            return ReceiptIcon;
  if (s.includes("servicio"))                           return AccountTreeIcon;
  if (s.includes("extracto") || s.includes("historico")) return HistoryIcon;
  if (s.includes("banco"))                              return AccountBalanceIcon;
  if (s.includes("pago"))                               return PaymentsIcon;
  if (s.includes("usuario"))                            return ManageAccountsIcon;
  if (s.includes("reporte") || s.includes("reportes")) return BarChartIcon;
  if (s.includes("historial"))                          return HistoryIcon;
  if (s.includes("adicion"))                            return AddCircleOutlineIcon;
  if (s.includes("devolucion"))                         return RemoveCircleOutlineIcon;
  if (s.includes("vehiculo"))                           return DirectionsCarIcon;
  if (s.includes("placa"))                              return LocationOnIcon;
  if (s.includes("indicador"))                          return SpeedIcon;
  if (s.includes("permiso"))                            return ManageAccountsIcon;
  if (s.includes("macrocuenta"))                        return PieChartIcon;
  if (s.includes("presupuesto"))                        return RequestPageIcon;
  if (s.includes("descripcion"))                        return ListAltIcon;
  if (s.includes("cheque"))                             return PaymentsIcon;
  if (s.includes("plataforma"))                         return AppsIcon;
  if (s.includes("sms"))                                return SmsIcon;
  if (s.includes("reclamo"))                            return SupportAgentIcon;
  if (s.includes("chatbot") || s.includes("chat"))      return SmartToyIcon;
  if (s.includes("equipo"))                             return DevicesIcon;
  if (s.includes("busqueda") || s.includes("buscar"))   return SearchIcon;
  if (s.includes("config"))                             return TuneIcon;
  return CircleIcon;
};

const LS_KEY     = "menu:ssr:v2";
const MAX_AGE_MS = 1000 * 60 * 60 * 6;

export default function NavTopResponsive() {
  const theme   = useTheme();
  const isMdUp  = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { compact = false } = useContext(ThemeContext);

  const appBarMinH = compact ? 38 : 46;
  const isDark     = theme.palette.mode === "dark";

  const handleNavigation = () => navigate("/dashboard");

  const [role, setRole]             = useState(null);
  const [loadingMenu, setLoading]   = useState(true);
  const [sections, setSections]     = useState([]);

  useEffect(() => {
    let mounted = true;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const isFresh = Date.now() - (parsed.ts || 0) < MAX_AGE_MS;
        if (parsed?.sections && mounted) {
          setRole(parsed.role ?? null);
          setSections(parsed.sections);
          if (isFresh) setLoading(false);
        }
      }
    } catch {}
    (async () => {
      try {
        const { data } = await axios.get("/api/menu");
        if (!mounted) return;
        const nextRole     = data?.role ?? null;
        const nextSections = Array.isArray(data?.sections) ? data.sections : [];
        setRole(nextRole);
        setSections(nextSections);
        localStorage.setItem(LS_KEY, JSON.stringify({ role: nextRole, sections: nextSections, ts: Date.now() }));
      } catch {}
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const visibleSections = useMemo(() => {
    if (loadingMenu) return [];
    return filterByRole(sections, role);
  }, [sections, role, loadingMenu]);

  const sectionIndex = useMemo(
    () => findSectionIndex(location.pathname, visibleSections),
    [location.pathname, visibleSections]
  );

  const [anchorEl, setAnchorEl]             = useState(null);
  const [openSection, setOpenSection]       = useState(null);
  const [mobileMenuAnchor, setMobileAnchor] = useState(null);

  const openMobileMenu  = (e) => setMobileAnchor(e.currentTarget);
  const closeMobileMenu = ()  => setMobileAnchor(null);

  const handleTabClick = (e, index) => {
    const section = visibleSections[index];
    if (!section) return;
    if (section.children?.length > 0) {
      setAnchorEl(e.currentTarget);
      setOpenSection(index);
    } else {
      setAnchorEl(null);
      setOpenSection(null);
      navigate(section.to);
    }
  };

  const handleMenuItemClick = (to) => {
    const current = location.pathname;
    setAnchorEl(null);
    setOpenSection(null);
    closeMobileMenu();
    if (current === to || current.startsWith(to + "/")) navigate(0);
    else navigate(to);
  };

  const handleClose = () => { setAnchorEl(null); setOpenSection(null); };

  const activeSection = openSection !== null ? visibleSections[openSection] : null;
  const activeColor   = activeSection ? getGroupColor(activeSection.label) : theme.palette.primary.main;

  return (
    <>
      <AppBar
        position="fixed"
        color="primary"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundImage: "none",
          borderBottom: `1px solid ${alpha("#fff", 0.08)}`,
          boxShadow: isDark ? "none" : "0 1px 8px rgba(0,0,0,0.10)",
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: appBarMinH,
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            gap: 1,
            px: { xs: 1, md: 1.5 },
          }}
        >
          {/* ── Logo ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {!isMdUp && (
              <IconButton color="inherit" onClick={openMobileMenu} size="small" sx={{ mr: 0.25 }}>
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
            <Box
              onClick={handleNavigation}
              sx={{ cursor: "pointer", opacity: 0.9, transition: "opacity 120ms", "&:hover": { opacity: 1 } }}
            >
              <Box
                component="img"
                src={MARCA.logoBlanco || logoTRS}
                alt={MARCA.nombre ? `${MARCA.nombre} Logo` : "Total Radio Systems Logo"}
                sx={{ height: MARCA.altoLogoBarra, width: "auto" }}
              />
            </Box>
          </Box>

          {/* ── Tabs desktop ── */}
          {isMdUp && visibleSections.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                overflowX: "auto",
                gap: 0.25,
                // ocultar scrollbar
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {visibleSections.map((section, index) => {
                const isActive      = sectionIndex === index;
                const isOpen        = openSection === index;
                const hasChildren   = section.children?.length > 0;
                const groupColor    = getGroupColor(section.label);
                const GroupIcon     = groupIconByLabel(section.label);

                return (
                  <Box
                    key={section.to || `tab-${index}`}
                    component="button"
                    onClick={(e) => handleTabClick(e, index)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.625,
                      px: 1.25,
                      py: 0.5,
                      height: appBarMinH - 8,
                      border: "none",
                      borderRadius: "7px",
                      cursor: "pointer",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      transition: "background-color 120ms ease",
                      // Activo: pill con fondo semitransparente
                      backgroundColor: isActive
                        ? alpha("#fff", 0.15)
                        : isOpen
                          ? alpha("#fff", 0.1)
                          : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? alpha("#fff", 0.18)
                          : alpha("#fff", 0.09),
                      },
                    }}
                  >
                    {/* Ícono del grupo con color */}
                    <GroupIcon
                      sx={{
                        fontSize: 15,
                        color: isActive ? "#fff" : alpha("#fff", 0.75),
                        // En activo mostramos el color del grupo con fondo
                        ...(isActive && {
                          bgcolor: groupColor,
                          borderRadius: "4px",
                          p: "2px",
                          fontSize: 16,
                          color: "#fff",
                        }),
                      }}
                    />

                    {/* Label */}
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 500,
                        letterSpacing: "0.02em",
                        color: isActive ? "#fff" : alpha("#fff", 0.72),
                        lineHeight: 1,
                      }}
                    >
                      {section.label}
                    </Typography>

                    {/* Chevron si tiene hijos */}
                    {hasChildren && (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 13,
                          color: isActive ? alpha("#fff", 0.8) : alpha("#fff", 0.45),
                          transition: "transform 150ms ease",
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          ml: -0.25,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Acciones ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifySelf: "end" }}>
            <GlobalSearchBox compact />
            <Notifications />
            <UserAvatar />
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Dropdown desktop ── */}
      {isMdUp && activeSection?.children?.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          elevation={0}
          MenuListProps={{ dense: true, disablePadding: true }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 210,
              borderRadius: "10px",
              border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.07)}`,
              boxShadow: isDark
                ? "0 12px 32px rgba(0,0,0,0.5)"
                : "0 8px 24px rgba(0,0,0,0.12)",
              bgcolor: isDark ? "grey.900" : "#fff",
              overflow: "hidden",
            },
          }}
        >
          {/* Header del dropdown con color del grupo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.06)}`,
              mb: 0.5,
            }}
          >
            {(() => {
              const Icon = groupIconByLabel(activeSection.label);
              return (
                <Box
                  sx={{
                    width: 26, height: 26,
                    borderRadius: "7px",
                    bgcolor: alpha(activeColor, 0.12),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 15, color: activeColor }} />
                </Box>
              );
            })()}
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", letterSpacing: "0.01em" }}>
              {activeSection.label}
            </Typography>
          </Box>

          {/* Items */}
          <Box sx={{ px: 0.625, pb: 0.625 }}>
            {activeSection.children.map((child) => {
              const isActive = location.pathname === child.to || location.pathname.startsWith(child.to + "/");
              const ChildIcon = childIconByLabel(child.label);
              return (
                <MenuItem
                  key={child.to}
                  onClick={() => handleMenuItemClick(child.to)}
                  sx={{
                    borderRadius: "7px",
                    px: 1.25,
                    py: 0.625,
                    minHeight: 34,
                    gap: 1.25,
                    mb: 0.125,
                    transition: "background-color 100ms ease",
                    backgroundColor: isActive ? alpha(activeColor, isDark ? 0.18 : 0.08) : "transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? alpha(activeColor, isDark ? 0.22 : 0.11)
                        : alpha(isDark ? "#fff" : "#000", 0.04),
                    },
                  }}
                >
                  {/* Ícono del hijo */}
                  <Box
                    sx={{
                      width: 24, height: 24, flexShrink: 0,
                      borderRadius: "6px",
                      bgcolor: isActive ? alpha(activeColor, 0.15) : alpha(isDark ? "#fff" : "#000", 0.05),
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <ChildIcon sx={{ fontSize: 14, color: isActive ? activeColor : "text.secondary" }} />
                  </Box>

                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? activeColor : "text.primary",
                      flexGrow: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {child.label}
                  </Typography>

                  {/* Indicador activo */}
                  {isActive && (
                    <CheckIcon sx={{ fontSize: 13, color: activeColor, opacity: 0.8, flexShrink: 0 }} />
                  )}
                </MenuItem>
              );
            })}
          </Box>
        </Menu>
      )}

      {/* ── Menu móvil ── */}
      {!isMdUp && (
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={closeMobileMenu}
          keepMounted
          elevation={0}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxHeight: "80dvh",
              borderRadius: "10px",
              border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.08)}`,
              boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.55)" : "0 8px 28px rgba(0,0,0,0.14)",
              bgcolor: isDark ? "grey.900" : "#fff",
              overflowY: "auto",
            },
          }}
          MenuListProps={{ dense: true, disablePadding: true, sx: { px: 0.625, py: 0.75 } }}
        >
          {visibleSections.map((section, index) => {
            const isParentActive = sectionIndex === index;
            const hasChildren    = section.children?.length > 0;
            const groupColor     = getGroupColor(section.label);
            const GroupIcon      = groupIconByLabel(section.label);

            return (
              <Box key={section.to || `m-${index}`}>
                {/* Encabezado de grupo */}
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 1.25, pt: index === 0 ? 0.5 : 1.25, pb: 0.375,
                  }}
                >
                  <Box
                    sx={{
                      width: 20, height: 20, borderRadius: "5px",
                      bgcolor: alpha(groupColor, 0.12),
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 12, color: groupColor }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: isParentActive ? groupColor : alpha(theme.palette.text.secondary, 0.6),
                    }}
                  >
                    {section.label}
                  </Typography>
                </Box>

                {/* Hijos o sección directa */}
                {hasChildren
                  ? section.children.map((child) => {
                      const isChildActive = location.pathname === child.to || location.pathname.startsWith(child.to + "/");
                      const ChildIcon = childIconByLabel(child.label);
                      return (
                        <MenuItem
                          key={child.to}
                          onClick={() => handleMenuItemClick(child.to)}
                          sx={{
                            borderRadius: "7px", px: 1.25, py: 0.5,
                            minHeight: 32, gap: 1, mb: 0.125,
                            backgroundColor: isChildActive ? alpha(groupColor, isDark ? 0.18 : 0.08) : "transparent",
                            "&:hover": {
                              backgroundColor: isChildActive
                                ? alpha(groupColor, 0.12)
                                : alpha(isDark ? "#fff" : "#000", 0.04),
                            },
                          }}
                        >
                          <ChildIcon sx={{ fontSize: 14, color: isChildActive ? groupColor : "text.disabled", flexShrink: 0 }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: isChildActive ? 600 : 400, color: isChildActive ? groupColor : "text.primary", flexGrow: 1 }}>
                            {child.label}
                          </Typography>
                          {isChildActive && <CheckIcon sx={{ fontSize: 12, color: groupColor, flexShrink: 0 }} />}
                        </MenuItem>
                      );
                    })
                  : (
                    <MenuItem
                      onClick={() => handleMenuItemClick(section.to)}
                      sx={{
                        borderRadius: "7px", px: 1.25, py: 0.625, minHeight: 34, mb: 0.125,
                        backgroundColor: isParentActive ? alpha(groupColor, isDark ? 0.18 : 0.08) : "transparent",
                        "&:hover": { backgroundColor: alpha(isDark ? "#fff" : "#000", 0.04) },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: isParentActive ? 600 : 400, color: isParentActive ? groupColor : "text.primary" }}>
                        {section.label}
                      </Typography>
                    </MenuItem>
                  )
                }

                {index < visibleSections.length - 1 && (
                  <Divider sx={{ my: 0.625, mx: 1, borderColor: alpha(isDark ? "#fff" : "#000", 0.06) }} />
                )}
              </Box>
            );
          })}
        </Menu>
      )}

      {/* Spacer */}
      <Box sx={{ height: appBarMinH }} />
    </>
  );
}
